import { asyncify, eachLimit } from 'async';
import { cloneDeep } from 'lodash';
import { StateCreator } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';

import { Sounds } from '../../constants/sounds';
// import {
//   GamePopupScreen,
//   GameScreen,
//   RootRoute,
// } from '../../constants/screenKey';
// import { SoundEffects } from '../../constants/soundEffect';
// import { downloadFiles } from '../../hooks/useFiles';
import { AudioPlayer } from '../../hooks/useAudioPlayer';
import { Balance, FileType, Uuid } from '../../models/Base';
import { StrokeCharacterData } from '../../models/FileStore';
import {
  ExtractDisplayDataFn,
  FetchDataFn,
  FinalizeRecordFn,
  GameControllerIntermediate,
  GameControllerRecordJson,
  GameControllerResult,
  GameControllerStoreAction,
  GameControllerStoreState,
  GamePlayParams,
  GamePlayResult,
  GamePlayState,
  GameScreen,
  InitializeRecordFn,
  PrepareIntermediateFn,
  RealGamePlayState,
  RefineResultFn,
  Utils,
} from '../../models/GameControllerStore';
import { ChallengeRecord, TextEvaluationWord } from '../../models/RecordStore';
import { Account, Exp, Profile, UserListType } from '../../models/UserStore';
// import { GameType } from '../../models/GameStore';
import { useFileStore } from '../../stores/FileStore';
// import { useRecordStore } from '../../stores/RecordStore';
// import { useSessionStore } from '../../stores/SessionStore';
// import {
//   SlideshowMode,
//   createSlideshowParams,
// } from '../../stores/SlideshowFlowStore';
import { useSpaceStore } from '../../stores/SpaceStore';
import { useTimer } from '../../stores/TimeStore';
// import { negativeNumberRecord } from '../../utils/object';
import { getStarsByScore } from '../../utils/record';
import { useSessionStore } from '../SessionStore';
import { useUserStore } from '../UserStore';

import { GameMode, GameParams } from './';

const getRandomInterval = (base = 15000, delta = 15000) =>
  base + Math.round(Math.random() * delta);

const shouldImprove = (
  evaluation: TextEvaluationWord[],
  { orange: orangePercentage = 0.2, red: redPercenteage = 0 } = {},
) => {
  let total = 0;
  // let cyan = 0;
  let orange = 0;
  let red = 0;
  evaluation.forEach((word) => {
    word.units.forEach((unit) => {
      if (unit.isBlank) {
        total += 1;
        if ((unit.score ?? 0) > 95) {
          // cyan += 1;
        } else if ((unit.score ?? 0) > 50) {
          orange += 1;
        } else {
          red += 1;
        }
      }
    });
  });
  return (
    total > 0 &&
    (orange > total * orangePercentage || red > red * redPercenteage)
  );
};

type Store = GameControllerStoreState & GameControllerStoreAction;

const INITIAL_STATE: GameControllerStoreState = {
  params: {} as GameParams<GameMode>,
  gameMode: undefined,
  progress: undefined,
  param: undefined,
  displayData: undefined,
  gameSequence: undefined,
  fileUrls: undefined,
  index: undefined,
  error: undefined,
  initializeRecord: () => {
    throw new Error('not setup yet');
  },
  finalizeRecord: () => {
    throw new Error('not setup yet');
  },
  timer: {
    start: () => {
      // do nothing
    },
    pause: () => {
      // do nothing
    },
    newLap: () => {
      // do nothing
    },
    reset: () => {
      // do nothing
    },
  },
  audioPlayer: new AudioPlayer(),
  useMic: false,
  useCamera: false,
  useSpeechRecognition: false,
  record: undefined,
  isBestInType: false,
  score: 0,
  scores: {},
  stagePointCounter: {
    count: 0,
    max: 0,
    hiddenCount: 0,
    hiddenMax: 0,
    flushed: false,
  },
  submitAudio: undefined,
  inAppEvaluations: [],
  audios: [],
  strokeDatas: {},
  isScoreKeeping: false,
  gameScreen: GameScreen.Loading,
  gamePlayState: GamePlayState.Idle,
  screenShotInfo: {
    index: 0,
    paths: [],
    shotTimeout: 2000,
  },
  nextGameId: undefined,
  hasNext: false,
  hasSlideshow: false,
  evaluationForImprovement: undefined,
  improvementPlayCount: 0,
  fullPlayCount: 0,
  isLoading: false,
};

export enum InGameSoundEffects {
  wrongSelection = 'wrongSelection',
  correctSelection = 'correctSelection',
  flipCard = 'flipCard',
  gameEndSound = 'gameEndSound',
  earnCurrency = 'earnCurrency',
  earnBread = 'earnBread',
  payCurrency = 'payCurrency',
}

const initializer: StateCreator<Store> = (set, get) => {
  const setIfProgress = (s: (s: Store) => Partial<Store>) => {
    set((state) => {
      if (state.progress !== undefined) {
        return s(state);
      }
      return state;
    });
  };

  const setup: GameControllerStoreAction['setup'] = async <
    Params extends GameParams<GameMode>,
    Data,
    Intermediate extends GameControllerIntermediate,
    Result extends GameControllerResult,
    RecordJson extends GameControllerRecordJson,
  >(
    utils: Utils,
    params: Params,
    extractDisplayData: ExtractDisplayDataFn<Params, Intermediate, Result>,
    prepareIntermediate: PrepareIntermediateFn<Params, Intermediate>,
    fetchData: FetchDataFn<Params, Data, Intermediate>,
    refineResult: RefineResultFn<Params, Data, Intermediate, Result>,
    initializeRecord: InitializeRecordFn<
      Params,
      Intermediate,
      Result,
      RecordJson
    >,
    finalizeRecord: FinalizeRecordFn<Params, Intermediate, Result, RecordJson>,
    audioPlayer: AudioPlayer,
    timer: ReturnType<typeof useTimer>,
  ) => {
    if (get().progress !== undefined) {
      throw new Error('Game Flow Already Started');
    }
    set(() => ({
      isLoading: true,
    }));

    const abortThrow = () => {
      if (utils.signal.aborted) {
        throw new Error('Game Flow Aborted');
      }
    };

    try {
      // FIXME: unnecessary if? classroomId and gameSetId already in params as string
      if ('classroomId' in params && typeof params.classroomId === 'string') {
        await useSpaceStore
          .getState()
          .fetchSpace('gamePlay', params.classroomId);
        if ('gameSetId' in params && typeof params.gameSetId === 'string') {
          await useSpaceStore.getState().fetchAlbum({
            fieldName: 'gamePlay',
            classroomId: params.classroomId,
            gameSetId: params.gameSetId,
            scope:
              'scope' in params && typeof params.scope === 'string'
                ? params.scope
                : undefined,
          });
          // await useRecordStore
          //   .getState()
          //   .fetchAlbumSummaries([params.gameSetId]);
        }
      }
    } catch (err) {
      set(() => ({ error: err as Error }));
      return;
    }

    // initialize
    let dataProgress = 0.1;
    let fileProgress = 0;
    set(() => ({
      gameScreen: GameScreen.GameStart,
      gameMode: utils.mode,
      audioPlayer,
      timer,
      progress: dataProgress * 0.4 + fileProgress * 0.6,
      params,
      displayData: extractDisplayData(utils, params),
    }));

    // prepareIntermediate
    let error: Error | undefined;
    let intermediate!: Intermediate;
    while (!intermediate && !error) {
      try {
        abortThrow();
        const value = await prepareIntermediate(utils, params);
        if ('intermediate' in value && value.intermediate !== undefined) {
          intermediate = value.intermediate;
        } else {
          params = { ...params, ...value };
        }
      } catch (err) {
        error = err as Error;
      }
    }
    if (error !== undefined) {
      setIfProgress(() => ({ error }));
      return;
    }
    dataProgress = 0.5;
    setIfProgress(() => ({
      progress: dataProgress * 0.4 + fileProgress * 0.6,
      params,
      displayData: extractDisplayData(utils, params, intermediate),
      useMic: intermediate.useMic ?? false,
      useCamera: intermediate.useCamera ?? false,
      useSpeechRecognition: intermediate.useSpeechRecognition ?? false,
    }));

    // fetchData
    let result!: Result;
    let count = 0;
    while (!result && !error) {
      try {
        abortThrow();
        const { progress, ...value } = await fetchData(
          utils,
          params,
          intermediate,
        );
        dataProgress = dataProgress + progress * 0.5;
        setIfProgress(() => ({
          progress: dataProgress * 0.4 + fileProgress * 0.6,
        }));
        if ('result' in value && value.result) {
          result = refineResult(utils, params, intermediate, value.result);
        } else {
          intermediate = { ...intermediate, ...value };
        }
      } catch (err) {
        error = err as Error;
      }
      if (
        ['forbidden for follower not in group'].includes(
          error?.message ?? '',
        ) &&
        count < 10
      ) {
        abortThrow();
        // give some delay for server update
        await new Promise((resolve) =>
          setTimeout(resolve, count * 1000 + 1000),
        );
        error = undefined;
      }
      count++;
    }
    if (error !== undefined) {
      setIfProgress(() => ({ error }));
      return;
    }
    const displayData = extractDisplayData(utils, params, intermediate, result);
    dataProgress = 1;
    let nextGameId: GameControllerStoreState['nextGameId'];
    const hasNext: GameControllerStoreState['hasNext'] = false;
    const hasSlideshow: GameControllerStoreState['hasSlideshow'] = false;
    // if ('session' in params && Array.isArray(params.session)) {
    //   const current = params.session[result.defaultIndex];
    //   const next = params.session[result.defaultIndex + 1];
    //   if (next?.gameId !== undefined) {
    //     nextGameId = next.gameId;
    //     hasNext = true;
    //   }
    //   if (current && typeof current.hasSlideshow === 'boolean') {
    //     hasSlideshow = current.hasSlideshow;
    //   }
    // }
    setIfProgress(() => ({
      progress: dataProgress * 0.4 + fileProgress * 0.6,
      params,
      displayData,
      gameSequence: result.gameSequence,
      index: result.defaultIndex,
      initializeRecord: (
        gameParams: GamePlayParams<GameControllerRecordJson>,
      ) =>
        initializeRecord(
          utils,
          params,
          intermediate,
          result,
          gameParams as GamePlayParams<RecordJson>,
        ),
      finalizeRecord: (gameResult: GamePlayResult<GameControllerRecordJson>) =>
        finalizeRecord(
          utils,
          params,
          intermediate,
          result,
          gameResult as GamePlayResult<RecordJson>,
        ),
      nextGameId,
      hasNext,
      hasSlideshow,
    }));

    if (result.records.userRecord) {
      setIfProgress(() => ({
        record: result.records.userRecord as GameControllerRecordJson,
      }));
    }

    abortThrow();
    const files = ([] as { fileType: FileType; fileId: Uuid }[]).concat(
      Object.keys(result.fileUrls.images).map((fileId) => ({
        fileType: FileType.Image,
        fileId,
      })),
      Object.keys(result.fileUrls.audios).map((fileId) => ({
        fileType: FileType.Audio,
        fileId,
      })),
      Object.keys(result.fileUrls.videos).map((fileId) => ({
        fileType: FileType.Video,
        fileId,
      })),
      Object.keys(result.fileUrls.resourceImages).map((fileId) => ({
        fileType: FileType.ResourceImage,
        fileId,
      })),
      Object.keys(result.fileUrls.strokes).map((fileId) => ({
        fileType: FileType.Stroke,
        fileId,
      })),
      Object.keys(result.fileUrls.ttsAudios).map((fileId) => ({
        fileType: FileType.TtsAudio,
        fileId,
      })),
    );
    let fileDownloaded = 0;

    await eachLimit(
      files,
      10,
      asyncify(
        async ({ fileType, fileId }: { fileType: FileType; fileId: Uuid }) => {
          await useFileStore
            .getState()
            .downloadFileUrl({ fileType, fileId }, utils.signal);
          const url = useFileStore
            .getState()
            .getFileDownload(`${fileType}_${fileId}`)
            ?.getVersion()?.url;
          if (url) {
            if (fileType === FileType.Image) {
              result.fileUrls.images[fileId] = url;
            } else if (fileType === FileType.Audio) {
              result.fileUrls.audios[fileId] = url;
            } else if (fileType === FileType.Video) {
              result.fileUrls.videos[fileId] = url;
            } else if (fileType === FileType.ResourceImage) {
              result.fileUrls.resourceImages[fileId] = url;
            } else if (fileType === FileType.Stroke) {
              result.fileUrls.strokes[fileId] = url;
            } else if (fileType === FileType.TtsAudio) {
              result.fileUrls.ttsAudios[fileId] = url;
            }

            fileDownloaded++;
            fileProgress = (fileDownloaded / files.length) * 0.8;
            setIfProgress(() => ({
              progress: dataProgress * 0.4 + fileProgress * 0.6,
            }));
          } else {
            error = new Error('Invalid File Path');
          }
        },
      ),
    );

    if (error !== undefined) {
      setIfProgress(() => ({ error }));
      return;
    }
    setIfProgress(() => ({ fileUrls: result.fileUrls }));
    abortThrow();

    fileProgress = 0.8;
    setIfProgress(() => ({
      progress: dataProgress * 0.4 + fileProgress * 0.6,
    }));

    // preload audio files
    // const audioFiles = result.files.reduce<Record<string, string>>(
    //   (acc, file) => {
    //     if (file.fileType === FileType.Audio) {
    //       const path = useFileStore
    //         .getState()
    //         .getFileDownload(file)
    //         ?.getVersion()?.uri;
    //       if (path) {
    //         acc[file.fileId] = path;
    //       } else {
    //         error = new Error('Invalid File Path');
    //       }
    //     }
    //     return acc;
    //   },
    //   {},
    // );
    if (error !== undefined) {
      setIfProgress(() => ({ error }));
      return;
    }
    abortThrow();
    await eachLimit(
      Object.entries(result.fileUrls.ttsAudios), // TODO: audio?
      10,
      asyncify(async ([id, url]: [string, string]) => {
        await audioPlayer.loadAudio(id, url);
      }),
    );

    // load sound effects
    await audioPlayer.loadAudio(
      InGameSoundEffects.wrongSelection,
      Sounds.WrongSelection,
    );
    await audioPlayer.loadAudio(
      InGameSoundEffects.correctSelection,
      Sounds.CorrectSelection,
    );
    await audioPlayer.loadAudio(InGameSoundEffects.flipCard, Sounds.FlipCard);
    await audioPlayer.loadAudio(
      InGameSoundEffects.earnBread,
      Sounds.EarnNonCurrencyReward,
    );
    await audioPlayer.loadAudio(
      InGameSoundEffects.earnCurrency,
      Sounds.EarnCurrency,
    );
    await audioPlayer.loadAudio(
      InGameSoundEffects.payCurrency,
      Sounds.PayCurrency,
    );

    const strokeDatas: Record<string, StrokeCharacterData> = {};

    await eachLimit(
      Object.entries(result.fileUrls.strokes),
      10,
      asyncify(async ([id, url]: [string, string]) => {
        const strokeData = await useFileStore
          .getState()
          .getStrokeDataFromUrl(url);
        if (strokeData) {
          strokeDatas[id] = strokeData;
        }
      }),
    );

    fileProgress = fileProgress + 0.2;
    setIfProgress(() => ({
      progress: dataProgress * 0.4 + fileProgress * 0.6,
      strokeDatas: strokeDatas,
    }));
    set(() => ({
      isLoading: false,
    }));
  };

  const cleanup: GameControllerStoreAction['cleanup'] = () => {
    setIfProgress(() => {
      // const screenshots = state.screenShotInfo.paths;
      // eachLimit(
      //   screenshots,
      //   100,
      //   asyncify(async (path: string) => {
      //     try {
      //       // await RNFS.unlink(path);
      //     } catch (e) {
      //       // `unlink` will throw an error, if the item to unlink does not exist
      //       console.warn(e);
      //     }
      //   }),
      // ).catch(console.warn);
      return INITIAL_STATE;
    });
  };

  const startScoreKeeping: GameControllerStoreAction['startScoreKeeping'] =
    async (fee) => {
      const {
        record,
        index,
        evaluationForImprovement,
        initializeRecord,
        timer,
      } = get();

      if (evaluationForImprovement === undefined) {
        const r = await initializeRecord({ record, index, fee });
        setIfProgress(() => ({
          record: r,
          isScoreKeeping: true,
          score: 50,
          gamePlayState: GamePlayState.Start,
        }));
      } else {
        setIfProgress(() => ({
          score: 50,
          gamePlayState: GamePlayState.Start,
        }));
      }

      timer.reset();
      // timer should not be reset when gamePlayState === End.
      // otherwise, it will trigger Game.tsx hook to upload again
    };

  const startStage: GameControllerStoreAction['startStage'] = (
    maxPoint,
    hiddenMax,
  ) => {
    setIfProgress((state) => ({
      stagePointCounter: {
        count: 0,
        max: maxPoint,
        hiddenCount: 0,
        hiddenMax: hiddenMax,
        flushed: false,
      },
      inAppEvaluations: [...state.inAppEvaluations, undefined],
      audio: [],
    }));
  };

  const addScore: GameControllerStoreAction['addScore'] = (increment) => {
    setIfProgress((state) => ({
      score: Math.min(Math.max(state.score + increment, 0), 100),
    }));
    return get().score;
  };

  const addStagePoint: GameControllerStoreAction['addStagePoint'] = (
    count,
    hiddenCount = 0,
  ) => {
    setIfProgress((state) => ({
      stagePointCounter: {
        ...state.stagePointCounter,
        count: state.stagePointCounter.count + count,
        hiddenCount: state.stagePointCounter.hiddenCount + hiddenCount,
      },
    }));
  };

  const setInAppEvaluation: GameControllerStoreAction['setInAppEvaluation'] = (
    evaluation,
  ) => {
    setIfProgress((state) => {
      const inAppEvaluations = state.inAppEvaluations.slice(0, -1);
      inAppEvaluations.push(evaluation);
      return { inAppEvaluations };
    });
  };

  const flushScore: GameControllerStoreAction['flushScore'] = (
    category = 'undefined',
  ) => {
    setIfProgress((state) => {
      const { flushed } = state.stagePointCounter;
      if (flushed) {
        return state;
      }
      const scores: typeof state.scores = {
        [category]: [state.score],
        ...state.scores,
      };
      // const scores: typeof state.scores = { [category]: [], ...state.scores };
      // if (typeof hiddenMax === 'number' && hiddenMax > 0) {
      //   scores[category]?.push(
      //     hiddenMax > 0 ? (hiddenCount / hiddenMax) * 100 : 0,
      //   );
      // } else {
      //   scores[category]?.push(max > 0 ? (count / max) * 100 : 0);
      // }
      return {
        ...state,
        scores,
        stagePointCounter: { ...state.stagePointCounter, flushed: true },
      };
    });
  };

  const endScoreKeeping: GameControllerStoreAction['endScoreKeeping'] = async (
    durationInMs,
  ) => {
    const {
      params,
      displayData,
      record,
      score,
      scores,
      submitAudio,
      inAppEvaluations,
      audios,
      isScoreKeeping,
      evaluationForImprovement,
      improvementPlayCount,
      fullPlayCount,
      finalizeRecord,
    } = get();
    set({ isLoading: true });

    if (displayData?.isChallenge) {
      let result: { record: GameControllerRecordJson };
      if (record) {
        result = await finalizeRecord({
          scores,
          durationInMs,
          submitAudio,
          inAppEvaluations,
          audios,
          record,
        });
      }

      setIfProgress(() => ({
        record: result?.record,
        scores: {},
        isScoreKeeping: false,
        inAppEvaluations: [],
      }));
    } else if (isScoreKeeping && record) {
      const result = await finalizeRecord({
        scores,
        durationInMs,
        submitAudio,
        inAppEvaluations,
        audios,
        record,
      });

      // generate dummy duration
      let newDisplayData = displayData;
      const opponent = displayData?.dummies?.opponents[0];
      if (opponent?.isPlayedByBot) {
        newDisplayData = cloneDeep(displayData);
        const min = 75; // TODO: depends on dummy level
        const max = 155; // TODO: depends on dummy level
        const ratio = (Math.random() * (max - min) + min) / 100;
        newDisplayData!.dummies!.opponents[0]!.durationInMs = Math.round(
          durationInMs * ratio,
        );
      }

      let evaluation = inAppEvaluations[0];
      if (params.isAssistedGame || !shouldImprove(evaluation?.word ?? [])) {
        evaluation = undefined;
      }

      setIfProgress(() => ({
        displayData: newDisplayData,
        record: result.record,
        isBestInType: result.isBestInType ?? false,
        scores: {},
        isScoreKeeping: false,
        evaluationForImprovement: evaluation,
      }));
    } else {
      if (record) {
        await finalizeRecord({
          scores,
          durationInMs,
          submitAudio,
          inAppEvaluations,
          audios,
          record,
        });
      }
      let evaluation = evaluationForImprovement;
      let i = improvementPlayCount;
      let f = fullPlayCount;
      const newEvaluation = inAppEvaluations[inAppEvaluations.length - 1];
      if (evaluationForImprovement) {
        i += 1;
        if (!shouldImprove(newEvaluation?.word ?? [], { orange: 0, red: 0 })) {
          evaluation = null;
        } else {
          evaluation = newEvaluation;
        }
      } else if (evaluationForImprovement === null) {
        f += 1;
        if (!shouldImprove(newEvaluation?.word ?? [])) {
          evaluation = undefined;
        } else {
          evaluation = newEvaluation;
        }
      }

      if (i >= 7 || f >= 3) {
        evaluation = undefined;
      }

      const r = cloneDeep(record); // FIXME: replace duration for UI now, may be using other method is better
      if (r?.gameRecord) {
        r.gameRecord.score = score;
        r.gameRecord.durationInMs = durationInMs;
      }

      setIfProgress(() => ({
        record: r,
        scores: {},
        evaluationForImprovement: evaluation,
        improvementPlayCount: i,
        fullPlayCount: f,
      }));
    }
    set({ isLoading: false });
  };

  const getScoreDisplay: GameControllerStoreAction['getScoreDisplay'] = () => {
    const { displayData, stagePointCounter, scores } = get();
    if (displayData?.isChallenge) {
      return stagePointCounter
        ? [
            `${stagePointCounter.count}(${stagePointCounter.max})`,
            stagePointCounter.count,
          ]
        : ['', 0];
    } else if (scores) {
      const total: number = Object.values(scores)
        .flat()
        .reduce((acc, cur) => acc + getStarsByScore(cur), 0);
      return [`${total}`, total];
    } else {
      return ['', 0];
    }
  };

  const getScoreInfo: GameControllerStoreAction['getScoreInfo'] = () => {
    const { displayData, stagePointCounter, scores } = get();
    if (!displayData?.isChallenge) {
      return stagePointCounter
        ? {
            count: stagePointCounter.count,
            total: stagePointCounter.max,
          }
        : { count: 0, total: 0 };
    } else if (scores) {
      const total = Object.values(scores)
        .flat()
        .reduce((acc, cur) => acc + getStarsByScore(cur), 0);
      return { count: 0, total };
    } else {
      return { count: 0, total: 0 };
    }
  };

  const setAudio: GameControllerStoreAction['setAudio'] = (audio, index) => {
    setIfProgress((state) => {
      if (typeof index === 'number') {
        // conversation
        if (state.audios.length <= index) {
          state.audios.push([]);
        }
        state.audios[index]?.push(audio);
      } else {
        // result
        state.audios.push([audio]);
      }
      return { audios: state.audios };
    });
  };

  const setSubmitAudio: GameControllerStoreAction['setSubmitAudio'] = (
    submitAudio,
  ) => {
    setIfProgress(() => ({ submitAudio }));
  };

  const onGameLoaded: GameControllerStoreAction['onGameLoaded'] = async () => {
    const { gameMode } = get();
    switch (gameMode) {
      case GameMode.Record:
        await startScoreKeeping({});
        await endScoreKeeping(Infinity);
        set(() => ({ gameScreen: GameScreen.GameEnd }));
        break;
    }
  };

  let isStarting = false;
  const { getCurrentUser } = useUserStore.getState();
  const onGameStart: GameControllerStoreAction['onGameStart'] = async () => {
    if (isStarting) return;
    isStarting = true;
    const { params, evaluationForImprovement } = get();

    if (
      !params.isAssistedGame &&
      !params.isRevisionGame &&
      evaluationForImprovement === undefined
    ) {
      getCurrentUser().catch(console.error);
      get().audioPlayer.playAudio(InGameSoundEffects.payCurrency);
      await new Promise((resolve) => setTimeout(resolve, 1200));
    }

    await startScoreKeeping({});
    set({ gameScreen: GameScreen.GamePlay });
    isStarting = false;

    // // const { reduceDailyAllowance, checkBalance } = useSessionStore.getState();
    // const fee = get().displayData?.fee ?? {};
    // // const ticket = fee.ticket ?? 0;
    // // check whether can be claimed by daily allowance
    // // const remaining = reduceDailyAllowance(fee, false);

    // // const payment = checkBalance(negativeNumberRecord(remaining));
    // // if (payment !== true) {
    // //   // no enough
    // //   return payment;
    // // }

    // // reduceDailyAllowance(fee, true);
    // const jumpPage = () => {
    //   /**
    //    * TournamentAuth : (tournamentPractice || tournament) && useCamera
    //    * CompetitionAnimation : (tournament && !useCamera) || competition
    //    * GameStage : default cases
    //    */
    //   const {
    //     gameMode = GameMode.Default,
    //     displayData: { dummies } = {},
    //     params,
    //   } = get();
    //   const useCamera: boolean =
    //     'useCamera' in params ? !!params.useCamera : false;
    //   let gameScreen: GameScreen;
    //   if (dummies) {
    //     gameScreen = GameScreen.GameStart;
    //   } else {
    //     gameScreen = GameScreen.GamePlay;
    //   }
    //   set(() => ({ gameScreen }));
    // };
    // return (startRewardAnimation) => {
    //   jumpPage();
    //   // if (Object.values(remaining).every((v) => !v)) {
    //   //   // no need to pay
    //   //   jumpPage();
    //   // } else {
    //   //   startRewardAnimation(negativeNumberRecord(remaining), jumpPage);
    //   // }
    // };
  };

  const onGameStageEnd: GameControllerStoreAction['onGameStageEnd'] = async (
    durationInMs,
  ) => {
    await endScoreKeeping(durationInMs);

    const { gameSequence, index, displayData, audioPlayer, record } = get();

    const _rewardCoin =
      (record?.gameRecord?.bonusReward?.breadSlice ?? 0) +
      (record?.gameRecord?.reward?.breadSlice ?? 0);

    const _rewardEXP =
      (record?.gameRecord?.bonusReward?.exp ?? 0) +
      (record?.gameRecord?.reward?.exp ?? 0);
    const userId = useSessionStore.getState().activeUserId;
    const userAccount = useUserStore.getState().getAccount(userId ?? '');
    const userProfile = useUserStore.getState().getProfile(userId ?? '');

    if (userAccount) {
      const updatedUserAccount = new Account({
        ...userAccount,
        balance: new Balance({
          ...userAccount.balance,
          breadSlice: userAccount.balance.breadSlice + _rewardCoin,
          exp: userAccount.balance.exp + _rewardEXP,
        }),
      });
      useUserStore
        .getState()
        .setAccounts([updatedUserAccount], UserListType.Current);
    }

    if (userProfile) {
      const updatedUserProfile = new Profile({
        ...userProfile,
        exp: new Exp({
          ...userProfile.exp,
          knowledge: {
            ...userProfile.exp.knowledge,
            games: userProfile.exp.knowledge.games + _rewardEXP,
          },
        }),
      });
      useUserStore
        .getState()
        .setProfiles([updatedUserProfile], UserListType.Current);
    }

    let stars;

    if (displayData?.isChallenge) {
      if (record?.challengeRecord) {
        const challengeRecord = new ChallengeRecord(record.challengeRecord);
        const score = challengeRecord.score;
        if (score) {
          if (score >= 95) {
            stars = 5;
          } else if (score >= 75) {
            stars = 4;
          } else if (score >= 50) {
            stars = 3;
          } else if (score >= 25) {
            stars = 2;
          } else if (score >= 1) {
            stars = 1;
          }
        } else {
          stars = 1;
        }
      }
    } else {
      if (record) {
        stars = record.gameRecord?.stars;
      } else {
        stars = 1;
      }
    }

    switch (stars) {
      case 5:
        await audioPlayer.loadAudio(
          InGameSoundEffects.gameEndSound,
          Sounds.FullMarkApplause,
        );

        break;
      case 4:
      case 3:
        await audioPlayer.loadAudio(
          InGameSoundEffects.gameEndSound,
          Sounds.NormalApplause,
        );
        break;
      default:
        await audioPlayer.loadAudio(
          InGameSoundEffects.gameEndSound,
          Sounds.FailedApplause,
        );
    }

    if (
      displayData?.isChallenge &&
      gameSequence &&
      index != undefined &&
      index + 1 < gameSequence?.length
    ) {
      set({ index: index + 1 });
      await onGameStart();
    } else {
      set({ gameScreen: GameScreen.GameEnd });
    }

    // const { gameSequence, displayData, gameMode } = get();
    // const noAnimation = false;
    // if (!displayData?.isChallenge) {
    //   endScoreKeeping(durationInMs);
    //   set(() => ({ gameScreen: GameScreen.GameEnd }));
    //   return true;
    // } else if (index + 1 < (gameSequence?.length ?? 0)) {
    //   // // use naviage instead of replace since it should be the same screen
    //   // navigation.navigate(GameScreen.GameStage, {
    //   //   index: index + 1,
    //   // });
    //   setIfProgress(() => ({ index: index + 1 }));
    //   setGamePlayState(GamePlayState.Reset).catch(console.error);
    //   return false;
    // } else {
    //   endScoreKeeping(durationInMs);
    //   set(() => ({ gameScreen: GameScreen.ChallengeEnd }));
    //   return true;
    // }
  };

  const onNextGame: GameControllerStoreAction['onNextGame'] = (navigate) => {
    const gameListsData = useSessionStore.getState().gameListsData;
    if (gameListsData) {
      const firstGame = gameListsData.gameLinkList[0];
      if (firstGame) {
        useSessionStore.getState().setGameListsData({
          ...gameListsData,
          gameLinkList: gameListsData.gameLinkList.slice(1),
        });
        cleanup();
        navigate(firstGame, { replace: true });
        return;
      } else {
        useSessionStore.getState().setGameListsData(null);
        navigate(-1);
        return;
      }
    } else {
      navigate(-1);
      return;
    }
  };

  const onLeaveGame: GameControllerStoreAction['onLeaveGame'] = (navigate) => {
    // const {
    //   chance = 1,
    //   unlockedNext,
    //   gameSetLinkWith,
    // } = get().displayData?.competition ?? {};
    // if (
    //   get().gameMode === GameMode.Tournament &&
    //   chance - 1 === 0 &&
    //   !unlockedNext &&
    //   (gameSetLinkWith?.linkWithChallenge !== undefined ||
    //     gameSetLinkWith?.linkWithNext !== undefined)
    // ) {
    //   navigate('/club/:classroomId/journey/:gameSetId/game/:gameId');
    // } else {
    //   navigate(-1);
    // }

    useSessionStore.getState().setGameListsData(null);
    cleanup();
    navigate(-1);
  };

  const onAnalysis: GameControllerStoreAction['onAnalysis'] = () => {
    const { record, gameMode } = get();
    const classroomId = record?.gameRecord?.classroomId;
    const gameSetId = record?.gameRecord?.gameSetId;
    // const gameId = record
    //   ? 'gameId' in record
    //     ? record.gameId
    //     : undefined
    //   : undefined;
    if (classroomId && gameSetId && gameMode) {
      set(() => ({ gameScreen: GameScreen.GameAnalysis }));
    }
  };

  const onRetry: GameControllerStoreAction['onRetry'] = (navigate) => {
    // const { params, gameMode } = get();
    // const p = createGameParams({
    //   ...params,
    //   mode:
    //     (gameMode === GameMode.Record ? GameMode.Default : gameMode) ??
    //     GameMode.Default,
    // });

    cleanup(); // remember to clear before navigate
    navigate('/club/:classroomId/journey/:gameSetId/game/:gameId');
  };

  const onNext: GameControllerStoreAction['onNext'] = (navigate) => {
    // const { params, hasNext, index, gameMode } = get();
    // if (hasNext && 'session' in params && Array.isArray(params.session)) {
    //   const nextGame = params.session[(index ?? 0) + 1];
    //   if (nextGame?.gameId) {
    //     const p = createGameParams({
    //       ...params,
    //       mode:
    //         (gameMode === GameMode.Record ? GameMode.Default : gameMode) ??
    //         GameMode.Default,
    //       gameId: nextGame.gameId,
    //       gameType: nextGame.gameType,
    //     });
    //   }
    // }
    cleanup(); // remember to clear before navigate
    navigate('/club/:classroomId/journey/:gameSetId/game/:gameId');
  };

  const onSlideshow: GameControllerStoreAction['onSlideshow'] = () => {
    // const { params, hasSlideshow, index } = get();
    // if (hasSlideshow && 'session' in params && Array.isArray(params.session)) {
    //   const game = params.session[index ?? 0];
    //   if (game) {
    //     const p = createSlideshowParams(
    //       game.slideshowId === undefined
    //         ? {
    //             ...params,
    //             gameId: game.gameId,
    //             slideshowId: undefined,
    //             mode: SlideshowMode.Default,
    //           }
    //         : {
    //             ...params,
    //             gameId: game.gameId,
    //             slideshowId: game.slideshowId,
    //             mode: SlideshowMode.Default,
    //           },
    //     );
    //     navigation.dispatch(() => {
    //       return StackActions.replace(RootRoute.Show, { params: p });
    //     });
    //   }
    // }
  };

  const onPractice: GameControllerStoreAction['onPractice'] = (navigate) => {
    const { displayData } = get();
    const gameSet = useSpaceStore
      .getState()
      .getAlbum(displayData?.gameSetId ?? '');
    if (gameSet) {
      navigate(
        '/club/:classroomId/journey/:gameSetId/game/:gameId?mode=tutorial',
      );
    }
  };

  const onSendImage: GameControllerStoreAction['onSendImage'] = () => {
    const { record, displayData, index } = get();
    if (record?.gameRecord && displayData && index !== undefined) {
      // const { classroomId, gameSetId } = displayData;
      // const { startedAt, type } = record.gameRecord;
      // const datas =
      //   data.base64?.match(/.{1,30720}/g)?.map((d, partNumber, array) => ({
      //     classroomId: classroomId,
      //     gameSetId: gameSetId,
      //     type,
      //     startedAt,
      //     partNumber,
      //     totalParts: array.length,
      //     mimeType: ImageMimeType.Jpeg,
      //     data: d,
      //   })) ?? [];
      // datas.forEach((d) => {
      //   // TODO: syncData challengeFootage
      //   console.log(d);
      //   // rootStore.records.syncData('challengeFootage', { ...d, index });
      // });
      setIfProgress(() => ({
        screenShotInfo: {
          index: index + 1,
          paths: [],
          shotTimeout: getRandomInterval(),
        },
      }));
    }
  };

  const setGamePlayState: GameControllerStoreAction['setGamePlayState'] =
    async (newState, delay = 0) => {
      const currentIndex = get().index ?? 0;
      const currentGame = get().gameSequence?.[currentIndex];
      const oldState = get().gamePlayState;
      let toState: RealGamePlayState;
      let action: () => void | Promise<void>;
      switch (newState) {
        case GamePlayState.Start:
          toState =
            oldState === GamePlayState.Idle ? GamePlayState.Start : oldState;
          action = () => {
            // do nothing
          };
          break;

        case GamePlayState.Run:
          toState =
            oldState === GamePlayState.Start ? GamePlayState.Run : oldState;
          action = () => {
            // do nothing
          };
          break;

        case GamePlayState.Running:
          toState =
            oldState === GamePlayState.Run ? GamePlayState.Running : oldState;
          action = () => {
            const { start, newLap } = get().timer;
            newLap(currentGame?.timeLimit ?? Infinity);
            start();
          };
          break;

        case GamePlayState.Pause:
          toState =
            oldState === GamePlayState.Running ? GamePlayState.Pause : oldState;
          action = () => {
            const { pause } = get().timer;
            pause();
          };
          break;

        case GamePlayState.Resume:
          toState =
            oldState === GamePlayState.Pause ? GamePlayState.Running : oldState;
          action = () => {
            const { start } = get().timer;
            start();
          };
          break;

        case GamePlayState.Score:
        case GamePlayState.ScoreOnly:
          toState =
            oldState === GamePlayState.Running ? GamePlayState.Score : oldState;
          action = async () => {
            const { pause } = get().timer;
            pause();
            flushScore();
            if (newState === GamePlayState.Score)
              await setGamePlayState(GamePlayState.WillEnd, 1000);
          };
          break;

        case GamePlayState.WillEnd:
          toState =
            oldState === GamePlayState.Score ? GamePlayState.WillEnd : oldState;
          action = async () => {
            // do something before clean action in Screen
            await setGamePlayState(GamePlayState.End);
          };
          break;

        case GamePlayState.End:
          toState =
            oldState === GamePlayState.WillEnd ? GamePlayState.End : oldState;
          action = async () => {
            // do nothing
          };
          break;

        case GamePlayState.Reset:
          toState =
            oldState === GamePlayState.End ? GamePlayState.Idle : oldState;
          action = async () => {
            await setGamePlayState(GamePlayState.Start);
          };
          break;
        case GamePlayState.Idle:
          toState = oldState;
          action = () => {
            // do nothing
          };
          break;
      }
      if (toState !== oldState) {
        console.log(`${oldState} -> ${toState}`);
        if (delay > 0) {
          await new Promise((r) => setTimeout(r, delay));
        }
        setIfProgress(() => ({
          gamePlayState: toState,
        }));
        await action();
      }
    };

  const initializeNewLap: GameControllerStoreAction['initializeNewLap'] = (
    timeLimit,
  ) => {
    const { timer, gamePlayState } = get();
    timer.newLap(timeLimit);
    if (gamePlayState === GamePlayState.Running) {
      timer.start();
    }
  };

  const playAudio: GameControllerStoreAction['playAudio'] = (id) => {
    if (id) {
      const { audioPlayer } = get();
      audioPlayer.playAudio(id);
    }
  };
  const stopAudio: GameControllerStoreAction['stopAudio'] = () => {
    const { audioPlayer } = get();
    audioPlayer.stopAudio();
  };

  const getAudio: GameControllerStoreAction['getAudio'] = (id) => {
    if (id) {
      const { audioPlayer } = get();
      return audioPlayer.getAudio(id)?.src;
    }
    return undefined;
  };

  return {
    ...INITIAL_STATE,
    setup,
    cleanup,
    startScoreKeeping,
    startStage,
    addScore,
    addStagePoint,
    setInAppEvaluation,
    flushScore,
    endScoreKeeping,
    getScoreDisplay,
    getScoreInfo,
    setAudio,
    setSubmitAudio,
    onGameLoaded,
    onGameStart,
    onGameStageEnd,
    onNextGame,
    onLeaveGame,
    onAnalysis,
    onRetry,
    onNext,
    onSlideshow,
    onPractice,
    onSendImage,
    setGamePlayState,
    initializeNewLap,
    playAudio,
    stopAudio,
    getAudio,
  };
};

export const useGameControllerStore = createWithEqualityFn<Store>()(
  initializer,
  Object.is,
);

export const useGameController = () => {
  return useGameControllerStore(
    (state) => ({
      setGamePlayState: state.setGamePlayState,
      initializeNewLap: state.initializeNewLap,
      startStage: state.startStage,
      addScore: state.addScore,
      addStagePoint: state.addStagePoint,
      setInAppEvaluation: state.setInAppEvaluation,
      playAudio: state.playAudio,
      stopAudio: state.stopAudio,
      getAudio: state.getAudio,
    }),
    () => true,
  );
};
